// CrewMessagesDrawer — the crew-facing private messaging panel (roadmap #6, PM epic — crew DM). A crew member
// opens it from the header "Messages" button to see their crew conversations, read/send private messages, and
// start a new one — with another teammate, a hand-picked group, or a whole role ("everyone in Admin"). All
// access is gated server-side by CrewDmCore (owns the event + conversation membership); this is presentation +
// calls. Messages render through the shared CommentBody (markdown + /commands), same as the comments drawer.
// Chat-style: oldest→newest, composer at the bottom. One drawer, three inner views: inbox · conversation · new.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Badge, Box, Button, Group, Loader, Paper, ScrollArea, Stack, Text, TextInput, Textarea, Tooltip } from "@mantine/core";
import { type OpResult } from "@sdflc/api-helpers";

import { CommentBody, CommentComposerHelp, isHelpCommand } from "@em/blocks";
import { THREAD_PAGE_SIZE, cursorOf, mergeNewestPage, mergeOlderPage } from "@em/utils";

import { AppDrawer, AppIcon, DiscardGuard, ReactionMenu } from "@/components/generic";
import { CrewDmService, EVENT_GUEST_ROLE_LABELS, type CrewDmConversation, type CrewDmDirectoryEntry, type CrewDmMessage } from "@/utils/api";
import { refusalReason } from "@/utils";

/** A live crew DM frame delivered over the crew SSE stream. Crew DMs are ordinary 'dm' channels (a crew DM is
 *  just a 'dm' channel with the admin's user:<userId> principal as a member), so frames carry entityKind='dm'. */
export interface CrewDmFrame {
  type?: string;
  entityKind?: string | null;
  channelId?: string | null;
  /** Present on a 'typing' frame — the display name of the person typing. */
  name?: string | null;
  /** Present on a 'reaction.updated' frame — the message + its fresh (viewer-agnostic) tallies. */
  commentId?: string | null;
  reactions?: { reaction: string; count: number }[];
  comment?: {
    id?: string | null;
    commenterName?: string | null;
    commenterKind?: string | null;
    commentText?: string | null;
    createdAt?: string | null;
  } | null;
}

interface CrewMessagesDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** Subscribe to live crew DM frames (owned by the launcher's single stream). Returns an unsubscribe. */
  subscribeDm: (handler: (frame: CrewDmFrame) => void) => () => void;
  /** Send a throttled "admin is typing" ping into a conversation (owned by the launcher, which holds the ticket). */
  sendTyping: (conversationId: string) => void;
}

// How often we send our own "I'm typing" ping while composing (fire-and-forget, throttled).
const TYPING_SEND_THROTTLE_MS = 2500;

const fmtWhen = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }

  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
};

const errText = (r: OpResult): string =>
  refusalReason(r) || "Something went wrong. Please try again.";

// A conversation's label = the OTHER participants' names (a DM is usually just one other person).
const conversationTitle = (c: CrewDmConversation): string => {
  // The event's Team conversation — everyone on the crew (the server keeps its members in step with the roster).
  if (c.isTeam) {
    return `Team · ${(c.participants || []).length}`;
  }

  const others = (c.participants || []).filter((p) => !p.isMe).map((p) => p.name || "Team member");
  if (others.length === 0) {
    return "You";
  }

  if (others.length <= 2) {
    return others.join(", ");
  }

  return `${others[0]}, ${others[1]} +${others.length - 2}`;
};

type View = "inbox" | "conversation" | "new";

interface RoleOption {
  role: string;
  roleName: string;
  count: number;
}

export function CrewMessagesDrawer({ opened, onClose, eventId, subscribeDm, sendTyping }: CrewMessagesDrawerProps) {
  const [view, setView] = useState<View>("inbox");
  const [error, setError] = useState<string | null>(null);

  // Inbox.
  const [conversations, setConversations] = useState<CrewDmConversation[]>([]);
  /**
   * ⚠ Whether the FIRST read has COME BACK — not the same as "not loading". The read starts in an
   * effect, which runs after the first paint, so a branch on `loading && rows.length === 0` shows
   * the empty state for one frame every time this opens. ▶ npm run check-list-states
   */
  const [conversationsLoaded, setConversationsLoaded] = useState(false);

  // Active conversation.
  const [active, setActive] = useState<CrewDmConversation | null>(null);
  const [messages, setMessages] = useState<CrewDmMessage[]>([]);
  /** Whether this thread's first read has come back — see `conversationsLoaded`. */
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  /** #128 — whether the last read came back FULL, so older messages may exist ("Load older"). */
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  /**
   * #128 — which OPENING of a conversation a read belongs to: bumped each time one opens, so an answer for an earlier
   * opening is dropped even when it is the same conversation (open A → Load older → back → A again: the old page would
   * land above the fresh newest page with a silent gap between).
   */
  const threadGenRef = useRef(0);
  /** The scroll height before older messages were added above — restored once they are on screen (layout effect). */
  const restoreScrollFromRef = useRef<number | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // "X is typing…" in the open conversation (auto-expires) + the throttle for our own outgoing typing pings.
  const [typingName, setTypingName] = useState<string | null>(null);
  const typingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSentRef = useRef(0);

  // New-message compose.
  const [query, setQuery] = useState("");
  const [allCrew, setAllCrew] = useState<CrewDmDirectoryEntry[]>([]);
  const [loadingCrew, setLoadingCrew] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<CrewDmDirectoryEntry[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [newText, setNewText] = useState("");
  const [starting, setStarting] = useState(false);

  // Refs so the live-stream handler always sees the current open conversation.
  const activeRef = useRef<CrewDmConversation | null>(null);
  activeRef.current = active;
  // …and the messages on screen, for the reconnect re-read to merge into (#128).
  const messagesRef = useRef<CrewDmMessage[]>([]);
  messagesRef.current = messages;

  const loadConversations = useCallback(async () => {
    const res = await CrewDmService.list(eventId);
    if (res.code === 0) {
      setConversations((Array.isArray(res.data) ? (res.data as CrewDmConversation[]) : []).filter((c) => !!c.id));
    }

    setConversationsLoaded(true);
  }, [eventId]);

  // On open, reset to the inbox and load.
  useEffect(() => {
    if (!opened) {
      return;
    }

    setView("inbox");
    setActive(null);
    setError(null);
    setConversationsLoaded(false);
    void loadConversations();
  }, [opened, loadConversations]);

  const loadMessages = useCallback(
    // `quiet`: a resync after a reconnect — keep the rows on screen while it re-reads (no loading flash).
    async (conversationId: string, quiet = false) => {
      if (!quiet) {
        setMessagesLoaded(false);
      }
      const gen = threadGenRef.current;
      const knownAtStart = new Set(messagesRef.current.map((m) => m.id));
      const viewport = viewportRef.current;
      // A reconnect must not pull someone reading older messages down to the bottom — only follow if they were there.
      const wasAtBottom = !viewport || viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 48;
      const res = await CrewDmService.thread({ eventId, conversationId, page: 1, pageSize: THREAD_PAGE_SIZE });

      // Another conversation (or another opening of this one) is on screen now — this answer is not for it.
      if (gen !== threadGenRef.current) {
        return;
      }

      if (res.code === 0) {
        // The server returns newest-first; show oldest→newest (chat order, newest at the bottom).
        const rows = (Array.isArray(res.data) ? (res.data as CrewDmMessage[]) : []).filter((c) => !!c.id);
        const page = rows.slice().reverse();
        const full = rows.length >= THREAD_PAGE_SIZE;

        if (quiet) {
          const merged = mergeNewestPage(messagesRef.current, page, full, knownAtStart);

          // Against the LATEST state: a live frame may be queued that the ref has not seen yet.
          setMessages((prev) => mergeNewestPage(prev, page, full, knownAtStart).messages);
          if (merged.hasOlder !== null) {
            setHasOlder(merged.hasOlder);
          }
        } else {
          setMessages(page);
          setHasOlder(full);
        }
      } else if (!quiet) {
        // A background re-read (a reconnect) that fails says nothing — what is on screen is still right.
        setError(errText(res));
      }

      setMessagesLoaded(true);
      if (!quiet || wasAtBottom) {
        requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: "end" }));
      }
    },
    [eventId]
  );

  const openConversation = useCallback(
    (c: CrewDmConversation) => {
      // The ref too, at once: the read below checks it when it answers, which may be before the next render.
      activeRef.current = c;
      threadGenRef.current += 1;
      setActive(c);
      setMessages([]);
      setHasOlder(false);
      setLoadingOlder(false);
      setText("");
      setError(null);
      setView("conversation");
      void loadMessages(c.id);
    },
    [loadMessages]
  );

  // #128/#133 — the page before the oldest message on screen. A CURSOR (that message: its id and createdAt), not page 2:
  // a message sent while this one is read would shift every offset. The scroll keeps its place: what was at the top
  // stays where it was, the older messages land above it.
  const loadOlder = useCallback(async () => {
    const conversationId = active?.id;
    const cursor = cursorOf(messagesRef.current[0]);

    if (!conversationId || !cursor || loadingOlder) {
      return;
    }

    const gen = threadGenRef.current;
    setLoadingOlder(true);
    const res = await CrewDmService.thread({ eventId, conversationId, pageSize: THREAD_PAGE_SIZE, ...cursor });

    // Another opening is on screen — it reset the spinner itself, and this page is not its history.
    if (gen !== threadGenRef.current) {
      return;
    }

    setLoadingOlder(false);

    if (res.code !== 0) {
      setError(errText(res));

      return;
    }

    // The top of the thread moved while this was read (a reconnect re-read replaced the screen): this page is older
    // than a message no longer shown, and merging it would leave a silent gap. The re-read decided hasOlder itself.
    if (messagesRef.current[0]?.id !== cursor.beforeId) {
      return;
    }

    const rows = (Array.isArray(res.data) ? (res.data as CrewDmMessage[]) : []).filter((c) => !!c.id);
    const merged = mergeOlderPage(messagesRef.current, rows);

    setHasOlder(merged.hasOlder);

    // Nothing new (only the boundary message again): nothing lands, so no scroll to restore — armed, it would jump the
    // view at the NEXT unrelated change of the list.
    if (merged.messages === messagesRef.current) {
      return;
    }

    restoreScrollFromRef.current = viewportRef.current?.scrollHeight ?? null;
    setMessages((prev) => mergeOlderPage(prev, rows).messages);
  }, [active, loadingOlder, eventId]);

  // Older messages landed above: keep what was on screen where it was (after React committed them, before paint).
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const from = restoreScrollFromRef.current;

    if (viewport && from !== null) {
      restoreScrollFromRef.current = null;
      viewport.scrollTop += viewport.scrollHeight - from;
    }
  }, [messages]);

  // Toggle a reaction on a message; the server returns the fresh summary (with our own "mine"), patched in place.
  const toggleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!active) {
        return;
      }
      const res = await CrewDmService.react({ eventId, conversationId: active.id, channelCommentId: messageId, reaction: emoji });
      if (res.code === 0 && Array.isArray(res.data) && res.data[0]) {
        const fresh = (res.data[0] as CrewDmMessage).reactions ?? [];
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions: fresh } : m)));
      }
    },
    [active, eventId]
  );

  // Merge live reaction tallies (viewer-agnostic counts) into a message, preserving our own "mine" flags.
  const mergeReactionCounts = useCallback((messageId: string, incoming: { reaction: string; count: number }[]) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) {
          return m;
        }
        const mineByEmoji = new Map((m.reactions ?? []).map((r) => [r.reaction, !!r.mine]));
        const reactions = incoming
          .filter((r) => r.count > 0)
          .map((r) => ({ reaction: r.reaction, count: r.count, mine: mineByEmoji.get(r.reaction) ?? false }));
        return { ...m, reactions };
      })
    );
  }, []);

  const renderReactions = (m: CrewDmMessage) => {
    const chips = m.reactions ?? [];
    return (
      <Group gap={4} mt={6} wrap="wrap" align="center">
        {chips.map((r) => (
          <Button
            key={r.reaction}
            size="compact-xs"
            variant={r.mine ? "light" : "default"}
            color={r.mine ? "blue" : "gray"}
            onClick={() => void toggleReaction(m.id, r.reaction)}
          >
            {r.reaction} {r.count}
          </Button>
        ))}
        <ReactionMenu
          position="top-start"
          target={
            <ActionIcon size="sm" variant="subtle" color="gray" aria-label="Add reaction">
              <AppIcon iconName="MoodSmile" size={16} />
            </ActionIcon>
          }
          onPick={(emoji) => void toggleReaction(m.id, emoji)}
        />
      </Group>
    );
  };

  // Live delivery: append incoming crew DMs to the open conversation + keep the inbox fresh. The server sends
  // DM frames only to participants (see handleCommentStream); our own just-sent message dedups by id.
  useEffect(() => {
    if (!opened) {
      return;
    }

    return subscribeDm((frame) => {
      // The live stream reconnected (a dropped connection, a fresh ticket) — it does not replay, so re-read what may
      // have arrived meanwhile: the open conversation, quietly, and the list.
      if (frame.type === "resync") {
        if (activeRef.current) {
          void loadMessages(String(activeRef.current.id), true);
        }
        void loadConversations();

        return;
      }

      // Live reaction tallies for the open conversation — merge counts, keep our own "mine".
      if (frame.entityKind === "dm" && frame.type === "reaction.updated") {
        const chId = frame.channelId ? String(frame.channelId) : "";
        if (frame.commentId && activeRef.current && chId && String(activeRef.current.id) === chId) {
          mergeReactionCounts(String(frame.commentId), Array.isArray(frame.reactions) ? frame.reactions : []);
        }
        return;
      }

      // "X is typing…" for the open conversation — show it briefly (a re-ping refreshes it).
      if (frame.entityKind === "dm" && frame.type === "typing") {
        const chId = frame.channelId ? String(frame.channelId) : "";
        if (activeRef.current && chId && String(activeRef.current.id) === chId) {
          setTypingName(frame.name || "Team member");
          if (typingClearRef.current) {
            clearTimeout(typingClearRef.current);
          }
          typingClearRef.current = setTimeout(() => setTypingName(null), 6000);
        }
        return;
      }

      if (frame.entityKind !== "dm" || frame.type !== "comment.created" || !frame.channelId || !frame.comment?.id) {
        return;
      }

      const chId = String(frame.channelId);
      if (activeRef.current && String(activeRef.current.id) === chId) {
        setTypingName(null);
        const incoming: CrewDmMessage = {
          id: String(frame.comment.id),
          commenterName: frame.comment.commenterName ?? null,
          commenterKind: frame.comment.commenterKind ?? null,
          commentText: frame.comment.commentText ?? null,
          hasReplies: false,
          createdAt: frame.comment.createdAt ?? null,
          reactions: [],
          mine: false,
          editable: false,
        };
        setMessages((prev) => (prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]));
        requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" }));
      }

      void loadConversations();
    });
  }, [opened, subscribeDm, loadConversations, loadMessages, mergeReactionCounts]);

  const send = useCallback(async () => {
    const body = text.trim();
    if (!body || !active) {
      return;
    }

    if (isHelpCommand(body)) {
      setText("");
      setHelpOpen(true);
      return;
    }

    setSending(true);
    setError(null);
    const res = await CrewDmService.post({ eventId, conversationId: active.id, commentText: body });
    setSending(false);
    if (res.code === 0) {
      const created = (Array.isArray(res.data) ? (res.data as CrewDmMessage[]) : []).filter((c) => !!c.id);
      setMessages((prev) => {
        // Reconcile with the live SSE echo of our OWN message (which arrives as not-"mine"): upgrade any
        // already-present row to this "You" version (matched by id), append only genuinely new ones — so our
        // message shows exactly once, as "You".
        const createdById = new Map(created.map((c) => [c.id, c]));
        const upgraded = prev.map((m) => createdById.get(m.id) ?? m);
        const known = new Set(prev.map((m) => m.id));
        const fresh = created.filter((c) => !known.has(c.id));
        return [...upgraded, ...fresh];
      });
      setText("");
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" }));
    } else {
      setError(errText(res));
    }
  }, [text, active, eventId]);

  const insertIntoComposer = useCallback((snippet: string) => {
    setText((prev) => {
      const sep = !prev || /\s$/.test(prev) ? "" : " ";
      return `${prev}${sep}${snippet}`;
    });
    requestAnimationFrame(() => composerRef.current?.focus());
  }, []);

  // Signal "the admin is typing" into the open conversation — throttled, fire-and-forget (the launcher holds the
  // stream ticket and does the POST; the server delivers it only to the other member(s)).
  const notifyTyping = useCallback(() => {
    if (!active) {
      return;
    }
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_SEND_THROTTLE_MS) {
      return;
    }
    lastTypingSentRef.current = now;
    sendTyping(active.id);
  }, [active, sendTyping]);

  // Clear the "typing" indicator (+ pending timer) whenever the open conversation changes or the drawer closes.
  useEffect(() => {
    setTypingName(null);
    return () => {
      if (typingClearRef.current) {
        clearTimeout(typingClearRef.current);
      }
    };
  }, [active?.id, opened]);

  // New message: load the full crew ONCE when entering the view. The role quick-picks + the searchable list are
  // both derived from this stable set (client-filtered below), so typing a name never shrinks the role options
  // or their counts (finding #2). The server directory caps at 30 — plenty for a crew roster.
  useEffect(() => {
    if (view !== "new") {
      return;
    }

    let cancelled = false;
    setLoadingCrew(true);
    void (async () => {
      const res = await CrewDmService.directory(eventId, null);
      if (!cancelled) {
        setAllCrew(res.code === 0 && Array.isArray(res.data) ? (res.data as CrewDmDirectoryEntry[]) : []);
        setLoadingCrew(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [view, eventId]);

  // The searchable list — client-filtered over the loaded crew by name.
  const filteredCrew = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return allCrew;
    }

    return allCrew.filter((m) => (m.name || "").toLowerCase().includes(q));
  }, [allCrew, query]);

  // Distinct roles present among the crew — drives the "message everyone in <role>" quick-picks.
  const roleOptions = useMemo<RoleOption[]>(() => {
    const byRole = new Map<string, RoleOption>();
    for (const m of allCrew) {
      // Every crew member is addressable now (a role pick reaches them all, even those who haven't visited yet).
      if (!m.role) {
        continue;
      }

      const existing = byRole.get(m.role);
      if (existing) {
        existing.count += 1;
      } else {
        byRole.set(m.role, { role: m.role, roleName: EVENT_GUEST_ROLE_LABELS[m.role] || m.role, count: 1 });
      }
    }

    return Array.from(byRole.values()).sort((a, b) => a.roleName.localeCompare(b.roleName));
  }, [allCrew]);

  const startNewView = useCallback(() => {
    setSelectedUsers([]);
    setSelectedRoles([]);
    setQuery("");
    setAllCrew([]);
    setNewText("");
    setError(null);
    setView("new");
  }, []);

  const toggleUser = useCallback((entry: CrewDmDirectoryEntry) => {
    setSelectedUsers((prev) => (prev.some((e) => e.id === entry.id) ? prev.filter((e) => e.id !== entry.id) : [...prev, entry]));
  }, []);

  const toggleRole = useCallback((role: string) => {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }, []);

  const canStart = selectedUsers.length > 0 || selectedRoles.length > 0;

  const startConversation = useCallback(async () => {
    if (!canStart) {
      return;
    }

    setStarting(true);
    setError(null);
    const res = await CrewDmService.start(eventId, {
      recipientEventGuestIds: selectedUsers.map((e) => e.id).filter((v): v is string => !!v),
      roles: selectedRoles,
    });
    if (res.code !== 0 || !Array.isArray(res.data) || !res.data[0]) {
      setStarting(false);
      setError(errText(res));
      return;
    }

    const convo = res.data[0] as CrewDmConversation;
    // Optionally send the first message right away — surface its error (don't silently drop the typed message).
    const body = newText.trim();
    let firstMessageError: string | null = null;
    if (body && !isHelpCommand(body)) {
      const postRes = await CrewDmService.post({ eventId, conversationId: convo.id, commentText: body });
      if (postRes.code !== 0) {
        firstMessageError = errText(postRes);
      }
    }

    setStarting(false);
    void loadConversations();
    openConversation(convo);
    // openConversation clears the error banner, so re-set it after (the conversation still opened).
    if (firstMessageError) {
      setError(firstMessageError);
    }
  }, [canStart, selectedUsers, selectedRoles, newText, eventId, loadConversations, openConversation]);

  const title = useMemo(() => {
    if (view === "conversation" && active) {
      return conversationTitle(active);
    }

    if (view === "new") {
      return "New message";
    }

    return "Messages";
  }, [view, active]);

  const drawerTitle = (
    <Group gap="xs" wrap="nowrap" align="center">
      {view !== "inbox" ? (
        // An unsent message asks before Back throws it away, as it does before the drawer closes (#124).
        <DiscardGuard>
          {(leave) => (
            <Tooltip label="Back" withArrow>
              <ActionIcon variant="subtle" color="gray" size="lg" radius="xl" aria-label="Back" onClick={() => leave(() => setView("inbox"))}>
                <AppIcon iconName="ChevronLeft" size={20} />
              </ActionIcon>
            </Tooltip>
          )}
        </DiscardGuard>
      ) : null}
      <Text fw={700} size="lg">
        {title}
      </Text>
    </Group>
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      // An unsent message is work — and so are the people picked for a new one: Esc / a click outside / the X / Back
      // ask before throwing it away (#123, #124).
      isDirty={(view === "conversation" && !!text.trim()) || (view === "new" && (!!newText.trim() || selectedUsers.length > 0 || selectedRoles.length > 0))}
      position="right"
      size="md"
      title={drawerTitle}
    >
      {error ? (
        <Text size="md" c="red" mb="sm">
          {error}
        </Text>
      ) : null}

      {/* INBOX */}
      {view === "inbox" ? (
        <Stack gap="md">
          <Button leftSection={<AppIcon iconName="Plus" size={16} />} onClick={startNewView}>
            New message
          </Button>

          {!conversationsLoaded ? (
            <Group justify="center" py="md">
              <Loader size="sm" />
            </Group>
          ) : conversations.length === 0 ? (
            <Text c="dimmed" ta="center" py="md" size="md">
              No conversations yet. Start one with “New message”.
            </Text>
          ) : (
            <Stack gap="xs">
              {conversations.map((c) => (
                <Paper key={c.id} withBorder radius="md" p="sm" style={{ cursor: "pointer" }} onClick={() => openConversation(c)}>
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text fw={600} size="md" truncate>
                        {conversationTitle(c)}
                      </Text>
                      <Text size="sm" c="dimmed" truncate>
                        {c.lastMessageMine ? "You: " : ""}
                        {c.lastMessageText || (c.isTeam ? "Everyone on the crew — say hello" : "No messages yet")}
                      </Text>
                    </Box>
                    {c.lastMessageAt ? (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                        {fmtWhen(c.lastMessageAt)}
                      </Text>
                    ) : null}
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      ) : null}

      {/* CONVERSATION */}
      {view === "conversation" && active ? (
        <Stack gap="sm" style={{ height: "100%" }}>
          <ScrollArea.Autosize mah="min(60vh, 460px)" type="auto" viewportRef={viewportRef}>
            {!messagesLoaded ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : messages.length === 0 ? (
              <Text c="dimmed" ta="center" py="md" size="md">
                No messages yet — say hello.
              </Text>
            ) : (
              <Stack gap="sm" px={2}>
                {hasOlder ? (
                  <Group justify="center">
                    <Button variant="subtle" size="md" loading={loadingOlder} onClick={() => void loadOlder()}>
                      Load older messages
                    </Button>
                  </Group>
                ) : null}
                {messages.map((m) => (
                  <Paper
                    key={m.id}
                    withBorder
                    radius="md"
                    p="sm"
                    style={{ maxWidth: "85%", alignSelf: m.mine ? "flex-end" : "flex-start", background: m.mine ? "var(--mantine-color-blue-light)" : undefined }}
                  >
                    <Group gap={8} mb={4} wrap="wrap" align="center">
                      <Text fw={600} size="sm">
                        {m.mine ? "You" : m.commenterName || "Team member"}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {fmtWhen(m.createdAt)}
                      </Text>
                    </Group>
                    <CommentBody text={m.commentText} />
                    {renderReactions(m)}
                  </Paper>
                ))}
                <div ref={bottomRef} />
              </Stack>
            )}
          </ScrollArea.Autosize>

          <Stack gap="xs">
            <Text size="xs" c="dimmed" style={{ minHeight: 18 }} aria-live="polite">
              {typingName ? `${typingName} is typing…` : ""}
            </Text>
            <Textarea
              ref={composerRef}
              value={text}
              onChange={(e) => {
                setText(e.currentTarget.value);
                notifyTyping();
              }}
              placeholder="Write a message…"
              autosize
              minRows={2}
              maxRows={6}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <Group justify="space-between" align="center" wrap="wrap" gap="xs">
              <CommentComposerHelp opened={helpOpen} onOpenedChange={setHelpOpen} onPick={insertIntoComposer} />
              <Button disabled={!text.trim()} loading={sending} onClick={() => void send()} leftSection={<AppIcon iconName="Send" size={16} />}>
                Send
              </Button>
            </Group>
          </Stack>
        </Stack>
      ) : null}

      {/* NEW MESSAGE */}
      {view === "new" ? (
        <Stack gap="md">
          {selectedUsers.length > 0 || selectedRoles.length > 0 ? (
            <Group gap="xs" wrap="wrap">
              {selectedRoles.map((role) => {
                const opt = roleOptions.find((r) => r.role === role);
                return (
                  <Badge
                    key={role}
                    size="lg"
                    variant="filled"
                    color="grape"
                    rightSection={
                      <ActionIcon size="xs" variant="transparent" color="gray" aria-label={`Remove ${opt?.roleName || "role"}`} onClick={() => toggleRole(role)}>
                        <AppIcon iconName="Plus" size={12} style={{ transform: "rotate(45deg)" }} />
                      </ActionIcon>
                    }
                  >
                    Everyone: {opt?.roleName || EVENT_GUEST_ROLE_LABELS[role] || role}
                  </Badge>
                );
              })}
              {selectedUsers.map((e) => (
                <Badge
                  key={e.id}
                  size="lg"
                  variant="light"
                  rightSection={
                    <ActionIcon size="xs" variant="transparent" color="gray" aria-label={`Remove ${e.name}`} onClick={() => toggleUser(e)}>
                      <AppIcon iconName="Plus" size={12} style={{ transform: "rotate(45deg)" }} />
                    </ActionIcon>
                  }
                >
                  {e.name}
                </Badge>
              ))}
            </Group>
          ) : null}

          {roleOptions.length > 0 ? (
            <Group gap="xs" wrap="wrap">
              {roleOptions.map((r) => {
                const picked = selectedRoles.includes(r.role);
                return (
                  <Button
                    key={r.role}
                    size="xs"
                    variant={picked ? "filled" : "light"}
                    color="grape"
                    onClick={() => toggleRole(r.role)}
                    leftSection={<AppIcon iconName={picked ? "CircleCheck" : "Users"} size={14} />}
                  >
                    Everyone in {r.roleName} ({r.count})
                  </Button>
                );
              })}
            </Group>
          ) : null}

          <TextInput
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="Search crew by name…"
            leftSection={<AppIcon iconName="User" size={16} />}
          />

          {loadingCrew ? (
            <Group justify="center" py="sm">
              <Loader size="xs" />
            </Group>
          ) : filteredCrew.length === 0 ? (
            <Text c="dimmed" ta="center" py="sm" size="md">
              {query.trim() ? "No matching crew." : "No crew on this event yet — add them on the Crew page."}
            </Text>
          ) : (
            <Stack gap={4}>
              {filteredCrew.map((r) => {
                const picked = selectedUsers.some((e) => e.id === r.id);
                const roleLabel = r.role ? EVENT_GUEST_ROLE_LABELS[r.role] || r.role : null;
                return (
                  <Button
                    key={r.id}
                    variant={picked ? "light" : "subtle"}
                    color={picked ? "blue" : "gray"}
                    justify="flex-start"
                    onClick={() => toggleUser(r)}
                    leftSection={<AppIcon iconName={picked ? "CircleCheck" : "User"} size={16} />}
                    rightSection={
                      roleLabel ? (
                        <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                          {roleLabel}
                        </Text>
                      ) : null
                    }
                  >
                    {r.name}
                  </Button>
                );
              })}
            </Stack>
          )}

          {canStart ? (
            <Stack gap="xs">
              <Textarea value={newText} onChange={(e) => setNewText(e.currentTarget.value)} placeholder="Write your first message… (optional)" autosize minRows={2} maxRows={6} />
              <Group justify="flex-end">
                <Button loading={starting} onClick={() => void startConversation()} leftSection={<AppIcon iconName="Send" size={16} />}>
                  Start conversation
                </Button>
              </Group>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </AppDrawer>
  );
}
